import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ModalTypes } from "configs/types";
import Button from "components/buttons/Button";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { Modal } from "components/Modal/Modal";
import { getBusinessErrorMessage } from "helpers/handleApiError";
import { getBonusImage } from "helpers/playerBonusFormatters";
import { useAppDispatch, useAppSelector } from "store/index";
import { closeModal, openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import {
  getPaymentInfo,
  getPlayerItem,
  handleDepositsClear,
  handleInfoClear,
} from "store/players/list/slice";
import { resetCompensationsList } from "store/payments/compensations/slice";
import { resetSelectPlayer } from "store/payments/selectPlayer/slice";
import { UserBonusOffer } from "types/playerBonuses";

import userIcon from "assets/icons/user.svg";
import bonusImageFallback from "assets/images/image_not_found.webp";
import styles from "./FinanceActionModal.module.scss";

type CompensationType =
  | "compensation"
  | "reward"
  | "other_credit"
  | "balance_transfer_credit"
  | "adjustment";

type FormField =
  | "compensationType"
  | "amount"
  | "wagerMultiplier"
  | "transactionCode"
  | "bonus"
  | "internalComment";

type FormErrors = Partial<Record<FormField, string>>;

/** Exact POST body for /payment/compensation — field names from TZ only */
type CompensationPayload = {
  userId: number;
  amount: string;
  wagerMultiplier: string;
  compensationType: CompensationType;
  transactionCode: string;
  bonusId?: number;
  userBonusId?: number;
  internalComment: string;
};

interface RewardBonusOffer extends UserBonusOffer {
  userBonusId?: number | null;
}

type SelectedBonusReference =
  | { bonusId: number; userBonusId?: never }
  | { bonusId?: never; userBonusId: number };

type CompensationResponse = {
  data?: {
    statusCode?: string;
  };
};

const COMPENSATION_TYPES: CompensationType[] = [
  "compensation",
  "reward",
  "other_credit",
  "balance_transfer_credit",
  "adjustment",
];

const DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;
const MORE_THAN_TWO_DECIMAL_PLACES_PATTERN = /^\d+\.\d{3,}$/;
const MIN_AMOUNT = 0.01;
const COMPLETED_STATUS = "completed";

const sanitizeDecimal = (value: string) => {
  const sanitized = value.replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = sanitized.split(".");
  const hasDecimalPoint = sanitized.includes(".");
  const decimalPart = decimalParts.join("");
  const normalizedIntegerPart =
    hasDecimalPoint && integerPart === "" ? "0" : integerPart;

  return hasDecimalPoint
    ? `${normalizedIntegerPart}.${decimalPart}`
    : normalizedIntegerPart;
};

const buildCompensationPayload = ({
  userId,
  amount,
  wagerMultiplier,
  compensationType,
  transactionCode,
  selectedBonusReference,
  internalComment,
}: {
  userId: number;
  amount: string;
  wagerMultiplier: string;
  compensationType: CompensationType;
  transactionCode: string;
  selectedBonusReference?: SelectedBonusReference;
  internalComment: string;
}): CompensationPayload => {
  const payload: CompensationPayload = {
    userId,
    amount,
    wagerMultiplier,
    compensationType,
    transactionCode: transactionCode.trim(),
    internalComment: internalComment.trim(),
  };

  if (compensationType === "reward" && selectedBonusReference) {
    Object.assign(payload, selectedBonusReference);
  }

  return payload;
};

const CreateCompensation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const player = useAppSelector((store) => store.players.payload);

  const [compensationType, setCompensationType] =
    useState<CompensationType>("compensation");
  const [amount, setAmount] = useState("");
  const [wagerMultiplier, setWagerMultiplier] = useState("");
  const [transactionCode, setTransactionCode] = useState("");
  const [bonusOffers, setBonusOffers] = useState<RewardBonusOffer[]>([]);
  const [selectedBonusKey, setSelectedBonusKey] = useState("");
  const [isBonusOffersLoading, setBonusOffersLoading] = useState(false);
  const [internalComment, setInternalComment] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setSubmitting] = useState(false);

  const userId = Number(searchParams.get("entityId"));
  const returnToList = searchParams.get("returnTo") === "list";
  const isReward = compensationType === "reward";
  const currency = player?.currency ?? "";

  const compensationOptions = useMemo(
    () =>
      COMPENSATION_TYPES.map((value) => ({
        value,
        label: t(`compensationType_${value}`),
      })),
    [t],
  );
  const bonusOptions = useMemo(
    () =>
      bonusOffers.map((bonus) => ({
        value:
          bonus.userBonusId !== undefined && bonus.userBonusId !== null
            ? `userBonus:${bonus.userBonusId}`
            : `bonus:${bonus.bonusId}`,
        label: bonus.title,
        img: getBonusImage(bonus) || bonusImageFallback,
      })),
    [bonusOffers],
  );

  useEffect(() => {
    if (userId && player?.id !== userId) {
      dispatch(getPlayerItem(userId));
    }
  }, [dispatch, player?.id, userId]);

  useEffect(() => {
    setSelectedBonusKey("");
    setBonusOffers([]);

    if (!isReward || !userId) {
      setBonusOffersLoading(false);
      return;
    }

    const abortController = new AbortController();
    setBonusOffersLoading(true);

    instance
      .get(`${apiRoutes.playerBonuses}/${userId}/offers`, {
        signal: abortController.signal,
      })
      .then((result) => {
        if (abortController.signal.aborted) return;

        const data = result?.data;
        setBonusOffers(Array.isArray(data) ? data : (data?.items ?? []));
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setBonusOffers([]);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setBonusOffersLoading(false);
        }
      });

    return () => abortController.abort();
  }, [isReward, userId]);

  const handleClose = () => {
    if (returnToList) {
      navigate(
        `${location.pathname}?activeModal=${ModalTypes.selectPlayer}&purpose=compensation`,
      );
      dispatch(openModal({ activeModal: ModalTypes.selectPlayer }));
      return;
    }

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${searchParams.get("entityId")}&tab=finance`,
    );
    dispatch(openModal({ activeModal: ModalTypes.player }));
  };

  const clearError = (field: FormField) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validateWagerMultiplierPrecision = (value: string) => {
    setErrors((current) => ({
      ...current,
      wagerMultiplier: MORE_THAN_TWO_DECIMAL_PLACES_PATTERN.test(value)
        ? t("maximumTwoDecimalPlaces")
        : undefined,
    }));
  };

  const getSelectedBonusReference = (): SelectedBonusReference | undefined => {
    if (!selectedBonusKey) return undefined;

    const [referenceType, id] = selectedBonusKey.split(":");
    const numericId = Number(id);

    return referenceType === "userBonus"
      ? { userBonusId: numericId }
      : { bonusId: numericId };
  };

  const validate = () => {
    const nextErrors: FormErrors = {};

    if (!compensationType) {
      nextErrors.compensationType = t("requiredField");
    }

    // Amount *: min 0.01; payload keeps the input string (no Number→string)
    if (!amount) {
      nextErrors.amount = t("requiredField");
    } else if (!DECIMAL_PATTERN.test(amount) || Number(amount) < MIN_AMOUNT) {
      nextErrors.amount = t("invalidCompensationAmount");
    }

    // Wager multiplier *: "0" is valid; empty is not
    if (!wagerMultiplier) {
      nextErrors.wagerMultiplier = t("requiredField");
    } else if (
      !DECIMAL_PATTERN.test(wagerMultiplier) ||
      Number(wagerMultiplier) < 0
    ) {
      nextErrors.wagerMultiplier = t("invalidCompensationWagerMultiplier");
    } else if (MORE_THAN_TWO_DECIMAL_PLACES_PATTERN.test(wagerMultiplier)) {
      nextErrors.wagerMultiplier = t("maximumTwoDecimalPlaces");
    }

    if (!transactionCode.trim()) {
      nextErrors.transactionCode = t("requiredField");
    }

    if (isReward && !selectedBonusKey) {
      nextErrors.bonus = t("requiredField");
    }

    if (!internalComment.trim()) {
      nextErrors.internalComment = t("requiredField");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getBackendError = (error: unknown) =>
    getBusinessErrorMessage(error, t, "compensationCreateError");

  const handleCreate = async () => {
    if (!userId || isSubmitting || !validate()) return;

    setSubmitting(true);
    try {
      const payload = buildCompensationPayload({
        userId,
        amount,
        wagerMultiplier,
        compensationType,
        transactionCode,
        selectedBonusReference: getSelectedBonusReference(),
        internalComment,
      });

      const response = (await instance.post(
        apiRoutes.paymentCompensation,
        payload,
      )) as CompensationResponse;

      if (response?.data?.statusCode !== COMPLETED_STATUS) {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("compensationCreateError"),
            type: NotificationTypes.error,
          }),
        );
        return;
      }

      dispatch(
        showNotification({
          title: t("success"),
          message: t("compensationCreated"),
          type: NotificationTypes.success,
        }),
      );

      if (returnToList) {
        dispatch(resetCompensationsList());
        dispatch(resetSelectPlayer());
        navigate(location.pathname);
        dispatch(closeModal());
        return;
      }

      dispatch(handleDepositsClear());
      dispatch(handleInfoClear());
      await Promise.all([
        dispatch(getPlayerItem(userId)),
        dispatch(getPaymentInfo(String(userId))),
      ]);
      handleClose();
    } catch (error) {
      dispatch(
        showNotification({
          title: t("error"),
          message: getBackendError(error),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={handleClose}>
      <Modal.Header className={styles.modalHeader}>
        <p>{t("createCompensation")}</p>
      </Modal.Header>
      <Modal.Content className={styles.modalContent}>
        <div className={styles.form}>
          <div className={styles.playerBar}>
            <img
              src={userIcon}
              alt=""
              aria-hidden="true"
              className={styles.playerBarIcon}
            />
            <div className={styles.playerBarMeta}>
              <div className={styles.playerBarItem}>
                <span className={styles.playerBarLabel}>{t("playerId")}:</span>
                <span className={styles.playerBarValue}>
                  {player?.id ?? userId ?? "—"}
                </span>
              </div>
              <span className={styles.playerBarDivider} aria-hidden="true" />
              <div className={styles.playerBarItem}>
                <span className={styles.playerBarLabel}>{t("nickname")}:</span>
                <span className={styles.playerBarValue}>
                  {player?.nickname || "—"}
                </span>
              </div>
            </div>
          </div>

          <DefaultDropdown
            label={t("compensationType")}
            value={compensationType}
            options={compensationOptions}
            onSelect={(value) => {
              const nextType = value as CompensationType;
              setCompensationType(nextType);
              if (nextType !== "reward") {
                setSelectedBonusKey("");
              }
              clearError("compensationType");
              clearError("bonus");
            }}
            isRequired
            hasError={Boolean(errors.compensationType)}
            disabled={isSubmitting}
            menuMaxHeight={170}
          />

          <InnerInput
            type="text"
            label={t("amount")}
            placeholder={t("enterAmount")}
            value={amount}
            onChange={(value) => {
              setAmount(sanitizeDecimal(value));
              clearError("amount");
            }}
            id="compensationAmount"
            inputMode="decimal"
            suffix={currency || undefined}
            isRequired
            hasError={Boolean(errors.amount)}
            title={errors.amount}
            disabled={isSubmitting}
          />

          <InnerInput
            type="text"
            label={t("wageringCoefficient")}
            placeholder={t("wageringCoefficientPlaceholder")}
            value={wagerMultiplier}
            onChange={(value) => {
              const sanitizedValue = sanitizeDecimal(value);
              setWagerMultiplier(sanitizedValue);
              validateWagerMultiplierPrecision(sanitizedValue);
            }}
            id="compensationWagerMultiplier"
            inputMode="decimal"
            isRequired
            tooltipText={t("wageringCoefficientTooltip")}
            tooltipPosition="right"
            hasError={Boolean(errors.wagerMultiplier)}
            title={errors.wagerMultiplier}
            disabled={isSubmitting}
          />

          <InnerInput
            type="text"
            label={t("transactionCode")}
            placeholder={t("confiscationTransactionCodePlaceholder")}
            value={transactionCode}
            onChange={(value) => {
              setTransactionCode(value);
              clearError("transactionCode");
            }}
            id="compensationTransactionCode"
            isRequired
            hasError={Boolean(errors.transactionCode)}
            title={errors.transactionCode}
            disabled={isSubmitting}
          />

          {isReward && (
            <DefaultDropdown
              label={t("rewardBonus")}
              placeholder={t("selectRewardBonus")}
              value={selectedBonusKey}
              options={bonusOptions}
              onSelect={(value) => {
                setSelectedBonusKey(String(value));
                clearError("bonus");
              }}
              loading={isBonusOffersLoading}
              emptyText={t("noBonusOffersAvailableForPlayer")}
              isRequired
              hasError={Boolean(errors.bonus)}
              disabled={isSubmitting}
              useFixedMenu
              menuMaxHeight={230}
              isEllipsis
            />
          )}

          <InnerInput
            type="textarea"
            label={t("internalComment")}
            placeholder={t("confiscationInternalCommentPlaceholder")}
            value={internalComment}
            onChange={(value) => {
              setInternalComment(value);
              clearError("internalComment");
            }}
            id="compensationInternalComment"
            rows={4}
            isRequired
            hasError={Boolean(errors.internalComment)}
            title={errors.internalComment}
            disabled={isSubmitting}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          isSecondary
          txt={returnToList ? t("back") : t("cancel")}
          handleClick={handleClose}
          disabled={isSubmitting}
        />
        <Button
          isGreen
          handleClick={handleCreate}
          txt={t("create")}
          disabled={!userId}
          isLoad={isSubmitting}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCompensation;
